import React from 'react';
import { Box, Typography, useTheme, Divider } from '@mui/material';
import { motion } from 'framer-motion';
import {
  Info as InfoIcon,
  Lock as LockIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { BookContainer, BookPage, BookNavigation } from '../components';
import { PAGE_NUMBERS } from '../utils/pageNumbers';
import { SEO } from '../utils/seo/SEO';

const AboutScreen: React.FC = () => {
  const theme = useTheme();

  const Section: React.FC<{
    title: string;
    icon: React.ReactNode;
    children: React.ReactNode;
    delay?: number;
  }> = ({ title, icon, children, delay = 0 }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.6 }}
    >
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          {icon}
          <Typography
            variant="h6"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 600,
              color: theme.palette.mode === 'dark'
                ? 'rgba(255, 248, 240, 0.9)'
                : 'rgba(139, 69, 19, 0.9)',
            }}
          >
            {title}
          </Typography>
        </Box>
        <Typography
          variant="body1"
          sx={{
            fontFamily: '"Crimson Text", serif',
            color: theme.palette.mode === 'dark'
              ? 'rgba(255, 248, 240, 0.8)'
              : 'rgba(139, 69, 19, 0.8)',
            fontSize: '1.1rem',
            lineHeight: 1.7,
          }}
        >
          {children}
        </Typography>
      </Box>
    </motion.div>
  );

  return (
    <>
      <SEO
        title="About Our AI Cooking Assistant"
        description="Discover Cook with Otto - the smart cooking assistant that makes meal planning easy. Learn how our AI helps you cook better, organize recipes, and become a confident home chef."
        url="/about"
        keywords={[
          'about Cook with Otto',
          'AI cooking technology',
          'smart cooking app',
          'cooking assistant features',
          'recipe management tool',
        ]}
      />
      <BookContainer>
        <Box sx={{ width: '100%', py: 4 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'center',
            alignItems: 'stretch',
            gap: 0,
            maxWidth: { xs: '100%', md: 1200 },
            margin: '0 auto',
          }}
        >
          {/* Left Page - About & Story */}
          <Box sx={{ width: { xs: '100%', md: '50%' }, minHeight: { xs: '50vh', md: 'auto' } }}>
            <BookPage isLeftPage={true} pageNumber={PAGE_NUMBERS.ABOUT_LEFT}>
              <Box
                sx={{
                  px: { xs: 3, md: 4 },
                  py: { xs: 3, md: 4 },
                }}
              >
                {/* Header */}
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: '"Playfair Display", serif',
                      fontWeight: 700,
                      color: theme.palette.mode === 'dark'
                        ? 'rgba(255, 248, 240, 0.9)'
                        : 'rgba(139, 69, 19, 0.9)',
                      mb: 1,
                      fontSize: { xs: '1.75rem', md: '2.125rem' },
                    }}
                  >
                    About Cook with Otto
                  </Typography>
                  <Divider
                    sx={{
                      mb: 4,
                      width: 100,
                      height: 2,
                      background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(90deg, rgba(255, 138, 101, 0.6), transparent)'
                        : 'linear-gradient(90deg, rgba(255, 107, 53, 0.6), transparent)',
                      border: 'none',
                    }}
                  />
                </motion.div>

                {/* Our Story Section */}
                <Section
                  title="Our Story"
                  icon={
                    <InfoIcon
                      sx={{
                        color: theme.palette.mode === 'dark' ? '#FF8A65' : '#FF6B35',
                      }}
                    />
                  }
                  delay={0.2}
                >
                  Cook with Otto was born from a simple idea: everyone should feel confident
                  in the kitchen. Whether you're preserving grandma's handwritten recipes or
                  trying a new dish from a magazine, Otto is your AI sous chef - ready to help
                  extract, organize, and perfect your recipes.
                </Section>

                {/* Mission Section */}
                <Section
                  title="Our Mission"
                  icon={
                    <InfoIcon
                      sx={{
                        color: theme.palette.mode === 'dark' ? '#6B9B7B' : '#4A7C59',
                      }}
                    />
                  }
                  delay={0.4}
                >
                  We combine cutting-edge AI with a beautiful cookbook experience to help you
                  capture any recipe, get answers to cooking questions, and build confidence
                  in the kitchen. Cooking should be joyful, not stressful.
                </Section>

                {/* Version Info Section */}
                <Section
                  title="Version Info"
                  icon={
                    <CodeIcon
                      sx={{
                        color: theme.palette.mode === 'dark'
                          ? 'rgba(255, 248, 240, 0.6)'
                          : 'rgba(139, 69, 19, 0.6)',
                      }}
                    />
                  }
                  delay={0.6}
                >
                  <Box component="span" sx={{ display: 'block', mb: 1 }}>
                    <strong>Version:</strong> 1.0.0
                  </Box>
                  <Box component="span" sx={{ display: 'block', mb: 1 }}>
                    <strong>Build Date:</strong> January 2025
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      fontStyle: 'italic',
                      opacity: 0.7,
                      mt: 2,
                    }}
                  >
                    © 2024 - Crafted with love
                  </Box>
                </Section>
              </Box>
            </BookPage>
          </Box>

          {/* Right Page - Features & Privacy */}
          <Box sx={{ width: { xs: '100%', md: '50%' }, minHeight: { xs: '50vh', md: 'auto' } }}>
            <BookPage isLeftPage={false} pageNumber={PAGE_NUMBERS.ABOUT_RIGHT}>
              <Box
                sx={{
                  px: { xs: 3, md: 4 },
                  py: { xs: 3, md: 4 },
                }}
              >
                {/* Key Features Section */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: '"Playfair Display", serif',
                      fontWeight: 600,
                      color: theme.palette.mode === 'dark'
                        ? 'rgba(255, 248, 240, 0.9)'
                        : 'rgba(139, 69, 19, 0.9)',
                      mb: 3,
                      fontSize: { xs: '1.5rem', md: '1.75rem' },
                    }}
                  >
                    Key Features
                  </Typography>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                >
                  <Box
                    component="ul"
                    sx={{
                      pl: 2,
                      mb: 4,
                      '& li': {
                        fontFamily: '"Crimson Text", serif',
                        fontSize: '1.1rem',
                        color: theme.palette.mode === 'dark'
                          ? 'rgba(255, 248, 240, 0.8)'
                          : 'rgba(139, 69, 19, 0.8)',
                        mb: 1.5,
                        lineHeight: 1.6,
                      },
                    }}
                  >
                    <li>Recipe extraction from photos with AI</li>
                    <li>Smart scaling and ingredient substitutions</li>
                    <li>Voice-guided cooking instructions</li>
                    <li>Automatic image generation for recipes</li>
                    <li>Personal recipe library with search</li>
                    <li>Step-by-step cooking assistance</li>
                  </Box>
                </motion.div>

                {/* Privacy & Data Section */}
                <Section
                  title="Privacy & Data"
                  icon={
                    <LockIcon
                      sx={{
                        color: theme.palette.mode === 'dark' ? '#6B9B7B' : '#4A7C59',
                      }}
                    />
                  }
                  delay={0.6}
                >
                  Your recipes are yours. We store them securely using Firebase, never sell
                  your data to third parties, and only use AI features when you request them.
                  Green Tokens ensure transparent pricing for AI-powered features.
                </Section>

                {/* Get in Touch Section */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8, duration: 0.6 }}
                >
                  <Box sx={{ mt: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: '"Playfair Display", serif',
                        fontWeight: 600,
                        color: theme.palette.mode === 'dark'
                          ? 'rgba(255, 248, 240, 0.9)'
                          : 'rgba(139, 69, 19, 0.9)',
                        mb: 2,
                      }}
                    >
                      Get in Touch
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: '"Crimson Text", serif',
                        color: theme.palette.mode === 'dark'
                          ? 'rgba(255, 248, 240, 0.7)'
                          : 'rgba(139, 69, 19, 0.7)',
                        fontSize: '1rem',
                      }}
                    >
                      Questions or feedback? We'd love to hear from you.
                    </Typography>
                  </Box>
                </motion.div>
              </Box>
            </BookPage>
          </Box>
        </Box>
      </Box>

      {/* Book Navigation */}
      <BookNavigation currentPageNumber={PAGE_NUMBERS.ABOUT_LEFT} />
    </BookContainer>
    </>
  );
};

export default AboutScreen;
